/* The manifesto: a letter from the makers, with the field-dispatch contact
   form at the end. Moved verbatim from player/src/App.tsx; shared with the
   launcher, which passes the site origin (item art, links) and its own API
   client for the contact form. */
import { memo, useState, type ReactNode } from 'react'
import { Send } from './Icons'
import type { ContactApi } from './types'

type Track = (action: string, label: string, value: string) => void

/* ------------------------------------------------------------------ Manifesto */
// Long-form letter from the makers. Editorial typography choices (and why):
//
//   - 64ch column width. Past ~75ch the eye fatigues mid-line; under ~50ch
//     the rhythm gets choppy. 64ch reads cleanly at 17-18px.
//   - 18px body type, line-height 1.8. Anything under 17 feels like UI, not
//     prose. The leading is generous on purpose; long-form needs air.
//   - Body color is dimmed to oklch(0.84). Pure white on near-black (the
//     default --foreground vs --background contrast) is ~18:1, which is
//     above WCAG AA but actively unpleasant for paragraph reading. Aim for
//     ~10:1 instead. Headings and accents stay near-white for hierarchy.
//   - No floated images in body. Floats interrupt reading flow and were the
//     biggest readability complaint on v1. Item icons now ride alongside each
//     section heading like a manuscript illumination, anchored to the
//     heading row instead of breaking the prose.
//   - No Card wrapper. Editorial copy reads better on the page background
//     than inside a panel; the panel was framing it like a UI widget.
//   - Rules between sections (subtle 1px line + dot) for visual rhythm.
//
// SEO goals carried over: ≥500 words, sub-12 word average sentence length,
// repeat H1 keywords ("find a DayZ server worth playing") in body, mention
// every major map / mode / mod surface.

// Whitespace-only section break for the manifesto. The line + dot affordance
// felt dated; just generous vertical air does the same pacing job, cleaner.
function Sep() { return <div aria-hidden className="my-10" /> }

// A floater image that respects the source PNG's natural aspect ratio. `max`
// is a MAX dimension on both axes — the image renders at its true ratio,
// just capped.
function FloaterImg({ src, alt, max }: { src: string; alt: string; max: number }) {
  const [ok, setOk] = useState(true)
  if (!ok) return null
  return (
    <img src={src} alt={alt} loading="lazy" decoding="async" onError={() => setOk(false)}
      style={{ maxWidth: max, maxHeight: max, width: 'auto', height: 'auto', display: 'block', opacity: 0.85 }} />
  )
}

// Ambient side-margin floaters for the manifesto.
//
// HARD RULE: these can never overlap the prose. The article column is locked
// to 64ch ≈ 727px (at 18px body), so its edges sit at 50% ± ~363px from the
// section center. Each floater is anchored to the *outer* edge of the column
// (left items via `right: calc(50% + 363px + gap)`, right items via mirror)
// so the floater's nearest edge sits `gap` pixels OUTSIDE the column at
// every viewport width.
//
// Hidden below xl (1280px). At that width the gutter on each side is
// (1280-727)/2 ≈ 276px, comfortably enough for a ~100px floater + buffer.
// Smaller viewports get nothing — better empty than crashing into the text.
function ManifestoFloaters({ origin }: { origin: string }) {
  const left: { src: string; alt: string; top: string; gap: number; max: number; rot: number; delay: number }[] = [
    { src: '/items/mountain-backpack.png',    alt: 'DayZ Mountain Backpack',    top: '4%',  gap: 24,  max: 92, rot:  -7, delay: 0    },
    { src: '/items/compass.png',              alt: 'DayZ Compass',              top: '28%', gap: 72,  max: 68, rot:  12, delay: 1200 },
    { src: '/items/handheld-transceiver.png', alt: 'DayZ Handheld Transceiver', top: '54%', gap: 36,  max: 82, rot:  -4, delay: 2400 },
    { src: '/items/glow-stick.png',           alt: 'DayZ Glow Stick',           top: '80%', gap: 88,  max: 64, rot:  18, delay: 3000 },
  ]
  const right: { src: string; alt: string; top: string; gap: number; max: number; rot: number; delay: number }[] = [
    { src: '/items/canned-baked-beans.png', alt: 'DayZ Canned Beans',   top: '10%', gap: 44, max: 76, rot:   6, delay: 600  },
    { src: '/items/hunting-knife.png',      alt: 'DayZ Hunting Knife',  top: '38%', gap: 96, max: 86, rot: -22, delay: 1800 },
    { src: '/items/canteen.png',            alt: 'DayZ Canteen',        top: '68%', gap: 32, max: 70, rot:   9, delay: 2600 },
    { src: '/items/oil-barrel.png',         alt: 'DayZ Oil Barrel',     top: '88%', gap: 60, max: 84, rot:  -8, delay: 3400 },
  ]
  // 363px ≈ half of the 727px (64ch @ 18px) article column. Any item whose
  // nearest edge sits at `50% + 363px + gap` is guaranteed outside the prose.
  const COL_HALF = 363
  const item = (it: typeof left[number], side: 'l' | 'r', i: number) => {
    const anchor = side === 'l'
      ? { right: `calc(50% + ${COL_HALF + it.gap}px)` }
      : { left:  `calc(50% + ${COL_HALF + it.gap}px)` }
    return (
      <div key={`${side}${i}`} className="absolute"
        style={{ top: it.top, ...anchor } as React.CSSProperties}>
        {/* Outer = rotation. Inner = bob animation. Both apply `transform`
            but to separate elements so neither clobbers the other. */}
        <div style={{ transform: `rotate(${it.rot}deg)`, transformOrigin: 'center center' }}>
          <div style={{
            animation: `floaty-bob 6s ease-in-out ${it.delay}ms infinite alternate`,
            willChange: 'transform',
          }}>
            <FloaterImg src={origin + it.src} alt={it.alt} max={it.max} />
          </div>
        </div>
      </div>
    )
  }
  return (
    <div aria-hidden className="hidden xl:block pointer-events-none absolute inset-0 z-0">
      {left.map((it, i) => item(it, 'l', i))}
      {right.map((it, i) => item(it, 'r', i))}
    </div>
  )
}

export const Manifesto = memo(function Manifesto({ api, track, origin = '' }: { api: ContactApi; track?: Track; origin?: string }) {
  // Body color sits well below pure white on purpose. Long-form prose at full
  // --foreground brightness (0.965) is fatiguing across hundreds of lines;
  // 0.70 lightness here still clears WCAG AA against the page background
  // (~7:1) but reads as ink, not laser. Headings stay near-white so they
  // pull rank visually, the lede sits in between as a soft on-ramp.
  const body = 'oklch(0.70 0.005 270)'
  const lede = 'oklch(0.82 0.004 270)'
  const heading = 'var(--foreground)'

  return (
    // Generous vertical air on both sides — the manifesto is a tonal shift
    // (editorial prose after a page of UI), and that shift only reads
    // cleanly with real breathing room. 280px each side gives it the feel
    // of a separate spread rather than a tacked-on section.
    <section aria-labelledby="manifesto-h" className="relative" style={{ marginTop: 280, marginBottom: 280 }}>
      {/* Ambient floaters drift in the gutters outside the prose column. They
          sit behind the article (z-0) and never receive pointer events, so
          they read as atmosphere, not chrome. */}
      <ManifestoFloaters origin={origin} />
      <article className="relative z-10 mx-auto px-4 sm:px-6 py-2"
        style={{ maxWidth: '64ch', color: body, fontSize: 18, lineHeight: 1.8 }}>
        {/* Pre-title eyebrow — small, mono, dim. Lets the H2 land cleaner. */}
        <p className="mono text-[10px] uppercase tracking-[.22em] text-[var(--muted)] mb-2">
          From the makers
        </p>
        <h2 id="manifesto-h"
          className="font-semibold tracking-tight"
          style={{ color: heading, fontSize: 'clamp(28px, 4vw, 38px)', lineHeight: 1.15, marginBottom: 18 }}>
          A letter to DayZ players
        </h2>
        {/* Lede — one notch larger than body, slightly brighter, to act as a
            visual on-ramp. */}
        <p style={{ fontSize: 20, lineHeight: 1.6, color: lede, marginBottom: 28 }}>
          Why we built dayzmetrics, what we promise you, and how we keep it
          honest.
        </p>

        <p className="mb-5">
          We built dayzmetrics because finding a DayZ server worth playing was
          harder than it should have been. The official list runs thousands of
          names deep. Many are ghost towns. Many are fakes. Sorting them ate
          the time we wanted to spend in Chernarus and Livonia.
        </p>
        <p className="mb-5">
          So we started tracking every public DayZ server in real time. We
          ping each one constantly. We log who plays, when they play, and for
          how long. The numbers on this page are fresh. They update while you
          read.
        </p>

        <Sep />

        <h3 className="font-semibold tracking-tight mb-3" style={{ color: heading, fontSize: 22, lineHeight: 1.2 }}>
          Why the fakes exist
        </h3>
        <p className="mb-5">
          DayZ has a problem older than most of its mods. Some hosts pad their
          player counts to climb the Steam list. A few report a full lobby
          every minute of every day, even when the server is empty. The trick
          draws traffic. It also wastes yours.
        </p>
        <p className="mb-5">
          Real servers have rhythm. Players log in after work, peak in the
          evening, and trail off overnight. Fake servers do not. Their
          populations are flat lines, impossible sawteeth, or numbers larger
          than the slot count. We match these patterns against ping behaviour,
          restart cycles and long-run history. Anything that fails the test is
          filtered out. You never see them in the list above.
        </p>
        {/* Honesty pass on our own detection — we aren't claiming a perfect
            filter, just a serious one that keeps getting sharper. */}
        <p className="mb-5">
          We are not 100% fake-proof. Anyone who tells you their detection is
          perfect is selling something. New padding tricks show up every
          month and a handful slip past us before we notice them. What we
          can promise is that we tune the detection constantly, retire old
          rules that fire on real servers, and add new ones the moment a
          pattern becomes clear. If you spot a server you think we are
          missing, the flag button at the top of each server page sends it
          straight to us.
        </p>

        <Sep />

        <h3 className="font-semibold tracking-tight mb-3" style={{ color: heading, fontSize: 22, lineHeight: 1.2 }}>
          How we rank a server
        </h3>
        <p className="mb-5">
          Population alone is a lazy ranking. A new community with five real
          friends and steady uptime beats a vanity 80-of-80 farm every time.
          So our Quality Score blends live players, time spent on the server,
          restart reliability and recent growth. The full breakdown sits
          behind the <i>how we rank</i> link next to the server table.
        </p>
        <p className="mb-5">
          And the whole list bends to you — narrow it by map, mods,
          perspective, region, play style or time of day, and star the servers
          you like so they are waiting next time. No account, no email, no
          tracking pixel.
        </p>

        <Sep />

        {/* New section: promoted servers. Opens with the honest "why we'd
            ever charge" — running real infrastructure costs real money —
            and only then gets to the rules that would bind us if we do. */}
        <h3 className="font-semibold tracking-tight mb-3" style={{ color: heading, fontSize: 22, lineHeight: 1.2 }}>
          On promoted servers
        </h3>
        <p className="mb-5">
          We run our own infrastructure to make this site work. Private
          servers ping every public DayZ lobby on a steady cycle. Every
          snapshot lands in our own database. We cross-reference several
          outside sources to filter the fakes, and process the lot every few
          minutes. None of it runs on a free tier. The bills are real, and
          they grow with the network.
        </p>
        <p className="mb-5">
          That is the honest reason we may, one day, accept paid promotion
          from a small number of hosts. We do not today. If we do, it will
          live by these rules, written here so we can be held to them.
        </p>
        {/* Pull-quote style ruleset. Each rule sits as its own short line so
            the list reads like a charter, not a paragraph. */}
        <ul className="space-y-3 mb-5 pl-0 list-none">
          {[
            ['Their own zone.', 'Promoted servers live in a clearly labelled zone with its own page and its own card design. The word "Promoted" sits front and centre, never tucked into a footnote.'],
            ['Never organic.', 'They will not appear in the rankings, the search results, the Trending strip, the New & Upcoming strip, or any "best of" surface. Paying us cannot move a server up.'],
            ['Same data, no exceptions.', 'Promotion changes nothing about a server\'s stats. The same fake-farm detection runs. The same ping, population and uptime numbers are reported. If a promoted server is caught farming, it is removed, the fee is refunded, and the incident is published.'],
            ['A real quality bar.', 'Not every server can buy promotion. Minimum age on the network. Zero detection flags in the prior 30 days. Real owner verification. A clean report history. If a host does not clear that bar, we do not list them at any price.'],
          ].map(([head, body]) => (
            <li key={head} className="flex gap-3">
              <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--accent)', opacity: 0.7 }} />
              <span>
                <strong className="font-semibold" style={{ color: heading }}>{head}</strong>{' '}
                {body}
              </span>
            </li>
          ))}
        </ul>

        <Sep />

        <h3 className="font-semibold tracking-tight mb-3" style={{ color: heading, fontSize: 22, lineHeight: 1.2 }}>
          The promise
        </h3>
        <p className="mb-5">
          dayzmetrics will stay free for players. The data will stay honest.
          We will keep filtering fakes harder, ranking smarter, and surfacing
          the servers worth your evening. No ads pasted on the organic
          server list. No pay-to-rank. No dark patterns. If we ever change
          course, you will read it here first, in writing this plain.
        </p>
        <p className="mb-5">
          Find a DayZ server worth playing. Drop into the lobby. Have a good
          night.
        </p>

        <p className="mono text-[11px] uppercase tracking-[.22em] mt-10" style={{ color: 'var(--muted)' }}>
          the dayzmetrics team
        </p>

        {/* F4 letter-style contact form — picked from /contact-test.html.
            Reads as a typewritten dispatch: TO / FROM / RE: / BODY rows on
            dashed underlines, mono typography, "Dispatch" submit verb. Posts
            to /api/contact, falls through to a thank-you state on success. */}
        <ContactLetter api={api} track={track} />
      </article>
    </section>
  )
})

/* ------------------------------------------------------------------ Contact letter */
// F4 "field dispatch" form. POSTs to /api/contact. Validation mirrors the
// backend (email + at least 8 chars of body). On success the whole card
// swaps for a thank-you state — we never tear the card open with a banner
// or push the layout, the swap happens in-place.
function ContactLetter({ api, track }: { api: ContactApi; track?: Track }) {
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [hp, setHp] = useState('') // honeypot — must stay empty
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  const bodyOk = body.trim().length >= 8

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    // The button is never silently disabled: an incomplete letter says what
    // is missing when Dispatch is pressed (the form is noValidate so this
    // line, not the browser's bubble, is the one message in both clients).
    if (!emailOk || !bodyOk) {
      setErr(!emailOk && !bodyOk ? 'Needs your email and a message of at least 8 characters.'
        : !emailOk ? 'Please enter a valid email address.'
        : 'Please write at least a short message (8 characters).')
      return
    }
    setSubmitting(true); setErr(null)
    try {
      const r = await api.contact(email.trim(), subject.trim(), body.trim(), hp)
      if (r.ok) {
        track?.('cta_click', 'manifesto_email', 'submitted')
        setDone(true)
      } else {
        setErr('Could not send. Please try again.')
      }
    } catch (e) {
      // Both clients attach the HTTP status and, for a 429, the Retry-After;
      // the message is the server's `detail` when it sent one, otherwise the
      // transport's own reason ("did not answer within 30 s", "Failed to
      // fetch"). Show the real reason, never a guess from the text.
      const { status, retryAfter, message } = e as Error & { status?: number; retryAfter?: number }
      // A bare "http NNN" carries nothing, and a browser's own fetch error
      // ("Failed to fetch", "Load failed", "NetworkError when attempting to
      // fetch resource.") is not a reason a visitor can act on; the launcher
      // sends a readable one instead ("did not answer within 30 s").
      const detail = message && !/^http \d{3}$/i.test(message) && !/^(Failed to fetch|Load failed|NetworkError)/.test(message)
        ? message : null
      if (status === 429) {
        // The endpoint's own daily cap carries a detail; a bare 429 is the
        // edge rate limit, which is over in seconds, not tomorrow.
        setErr(detail ?? `Too many requests right now. Try again in ${retryAfter ?? 20} s.`)
      } else if (status === 400) {
        setErr(detail ?? 'Please check the email and message.')
      } else if (status) {
        setErr(`Could not send (${status}${detail ? `: ${detail}` : ''}). Please try again in a bit.`)
      } else {
        setErr(`Could not send: ${detail ?? 'no connection'}. Please try again in a bit.`)
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <div className="mt-16 rounded-2xl mono"
           style={{ background: 'var(--background-secondary)' }}>
        <div className="text-[10px] uppercase tracking-[.22em] px-6 py-3 border-b border-[var(--border)] flex justify-between"
             style={{ color: 'var(--muted)' }}>
          <span><b style={{ color: 'var(--brand)' }}>DISPATCH</b> / received</span>
          <span>thank you</span>
        </div>
        <div className="p-8 sm:p-10 text-center">
          <div className="text-2xl mb-3" style={{ color: 'var(--foreground)', fontFamily: "'Geist', sans-serif", fontWeight: 600 }}>
            Got it. A real person will reply.
          </div>
          <p className="text-[14px] mx-auto max-w-[48ch]" style={{ color: 'var(--muted)', fontFamily: "'Geist', sans-serif", lineHeight: 1.6 }}>
            Most replies land within a day or two. If we need a follow-up we will write back to <span style={{ color: 'var(--foreground)' }}>{email}</span>.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-16 rounded-2xl mono"
         style={{ background: 'var(--background-secondary)' }}>
      <div className="text-[10px] uppercase tracking-[.22em] px-4 sm:px-6 py-3 border-b border-[var(--border)] flex flex-wrap items-center justify-between gap-x-3 gap-y-1"
           style={{ color: 'var(--muted)' }}>
        <span><b style={{ color: 'var(--brand)' }}>FIELD DISPATCH</b> / inbound channel</span>
        <span>open · 2 day reply</span>
      </div>
      <form onSubmit={submit} noValidate className="p-4 sm:p-8">
        {/* Honeypot — hidden from humans, bots tend to fill every visible field.
            Server treats any value here as a silent accept (no error, no insert). */}
        <input type="text" name="company" value={hp} onChange={(e) => setHp(e.target.value)}
               tabIndex={-1} autoComplete="off"
               style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
               aria-hidden />

        <LetterRow label="To">
          {/* Always-shown destination address. Sized to match the inputs and
              given `break-all` so it can wrap on the smallest phones instead
              of pushing the whole letter card off-screen. */}
          <span className="text-[13px] sm:text-[14px] break-all" style={{ color: 'var(--brand)' }}>
            support@dayzmetrics.com
          </span>
        </LetterRow>
        <LetterRow label="From">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                 placeholder="your email" autoComplete="email" aria-label="Your email address"
                 className="bg-transparent border-0 outline-none w-full text-[14px] py-1"
                 style={{ color: 'var(--foreground)' }} />
        </LetterRow>
        <LetterRow label="Re:">
          <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                 placeholder="subject line (optional)" maxLength={200} aria-label="Subject"
                 className="bg-transparent border-0 outline-none w-full text-[14px] py-1"
                 style={{ color: 'var(--foreground)' }} />
        </LetterRow>
        <LetterRow label="Body" align="top">
          <textarea required value={body} onChange={(e) => setBody(e.target.value)}
                    placeholder="Dear dayzmetrics," rows={6} maxLength={4000} aria-label="Message"
                    className="bg-transparent border-0 outline-none w-full text-[14px] py-1 resize-y"
                    style={{ color: 'var(--foreground)', minHeight: 120, lineHeight: 1.6 }} />
        </LetterRow>

        <div className="flex items-center justify-between gap-3 mt-5 pt-5 border-t border-[var(--border)] flex-wrap">
          <span className="text-[10px] uppercase tracking-[.18em] min-w-0" style={{ color: 'var(--muted)' }}>
            {err ? <span role="alert" className="normal-case tracking-normal text-[12px]" style={{ color: 'var(--color-danger, #e2574c)' }}>{err}</span>
                 : 'priority · standard · reply within 48h'}
          </span>
          <button type="submit" disabled={submitting}
                  className="inline-flex items-center gap-2 mono text-xs uppercase tracking-wider rounded-full px-5 py-3 transition-colors bg-[var(--brand-soft)] text-[var(--brand)] ring-1 ring-[var(--brand)] font-semibold hover:bg-[color-mix(in_oklab,var(--brand)_28%,transparent)] disabled:opacity-45 disabled:cursor-not-allowed [--icon-accent:var(--brand)]">
            <Send size={16} noFloor />
            {submitting ? 'Dispatching…' : 'Dispatch'}
          </button>
        </div>
      </form>
    </div>
  )
}

function LetterRow({ label, align = 'baseline', children }: {
  label: string; align?: 'baseline' | 'top'; children: ReactNode
}) {
  return (
    <div className="flex gap-2 sm:gap-3 py-2 border-b border-dashed border-[var(--border)]"
         style={{ alignItems: align === 'top' ? 'flex-start' : 'baseline' }}>
      {/* Tighter label column on mobile so the value side has every pixel it
          can to fit long content (the support email in particular). */}
      <span className="text-[10px] uppercase tracking-[.14em] w-12 sm:w-16 shrink-0"
            style={{ color: 'var(--muted)', paddingTop: align === 'top' ? 8 : 0 }}>
        {label}
      </span>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  )
}
